/**
 * scheduleExport.js — CSV schedule exports
 *
 * wallSchedule(st)    → CSV of all walls with material, dimensions, cost
 * openingSchedule(st) → CSV of all doors/windows with dimensions
 * roomSchedule(st)    → CSV of all rooms with area, perimeter, cost
 * fullBOQ(st)         → CSV bill-of-quantities summary
 */
import { getWallMetrics, getRoomMetrics } from "./metrics";
import { OPENING_PRESETS } from "../constants";

// ─── CSV utilities ────────────────────────────────────────────────────────────

function row(...cells) {
  return cells.map(c => {
    const s = String(c ?? "");
    return s.includes(",") || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
  }).join(",");
}

function downloadCSV(content, filename) {
  const blob = new Blob(["\uFEFF" + content], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ─── Wall schedule ────────────────────────────────────────────────────────────

export function wallSchedule(st, filename = "wall-schedule.csv") {
  const header = row(
    "Wall ID", "Material", "Length (mm)", "Thickness (mm)", "Height (mm)",
    "Gross Vol (m³)", "Net Vol (m³)", "Openings",
    "Bricks", "Cement (bags)", "Sand (cft)",
    "Mat Cost (₹)", "Labour Cost (₹)", "Total Cost (₹)"
  );

  const lines = Object.keys(st.edges).map(eId => {
    const m = getWallMetrics(eId, st);
    if (!m) return null;
    return row(
      eId.slice(0, 10),
      m.mat.name,
      m.lenMm,
      m.thickMm,
      m.heightMm,
      m.grossVol.toFixed(3),
      m.netVol.toFixed(3),
      m.ops.length,
      m.bricks,
      m.cement,
      m.sand,
      Math.round(m.matCost),
      Math.round(m.labourCost),
      Math.round(m.totalCost)
    );
  }).filter(Boolean);

  downloadCSV([header, ...lines].join("\n"), filename);
}

// ─── Opening schedule ─────────────────────────────────────────────────────────

export function openingSchedule(st, filename = "opening-schedule.csv") {
  const header = row(
    "ID", "Wall ID", "Type", "Width (mm)", "Height (mm)", "Sill Height (mm)",
    "Area (m²)", "Position (t)"
  );

  const lines = Object.values(st.openings || {}).map(op => {
    const typeLabel = Object.entries(OPENING_PRESETS).find(
      ([, v]) => v.width === op.width && v.height === op.height
    )?.[1]?.label || (op.width >= 900 && op.height >= 2000 ? "Door" : "Window");

    const area = (op.width / 1000 * op.height / 1000).toFixed(2);
    return row(
      op.id.slice(0, 10),
      op.edgeId.slice(0, 10),
      typeLabel,
      op.width,
      op.height,
      op.sillHeight || 0,
      area,
      op.t?.toFixed(3) || "0.500"
    );
  });

  downloadCSV([header, ...lines].join("\n"), filename);
}

// ─── Room schedule ────────────────────────────────────────────────────────────

export function roomSchedule(st, filename = "room-schedule.csv") {
  const header = row(
    "Room", "Type", "Area (m²)", "Perimeter (mm)",
    "Wall Finish Area (m²)", "Floor Cost (₹)", "Wall Finish Cost (₹)", "Total Cost (₹)"
  );

  const lines = Object.keys(st.faces || {}).map(faceId => {
    const m = getRoomMetrics(faceId, st);
    if (!m) return null;
    return row(
      m.rd.name || m.rd.type || "Room",
      m.rd.type || "ROOM",
      m.areaM2.toFixed(2),
      Math.round(m.perimMm),
      m.wallAreaM2.toFixed(2),
      Math.round(m.floorCost),
      Math.round(m.wallCost),
      Math.round(m.totalCost)
    );
  }).filter(Boolean);

  if (lines.length === 0) lines.push(row("(No labelled rooms)", "", "", "", "", "", "", ""));
  downloadCSV([header, ...lines].join("\n"), filename);
}

// ─── Full BOQ ─────────────────────────────────────────────────────────────────

export function fullBOQ(st, filename) {
  const projectName = st.project?.name || "Untitled";
  const safeName    = projectName.replace(/[^a-zA-Z0-9_-]/g, "_");
  const outFile     = filename || `${safeName}-BOQ.csv`;

  const lines = [
    row("PROJECT BILL OF QUANTITIES"),
    row("Project", projectName),
    row("Client",  st.project?.client  || ""),
    row("Address", st.project?.address || ""),
    row("Date",    st.project?.date    || new Date().toLocaleDateString()),
    row(""),
    row("SECTION", "ITEM", "QTY", "UNIT", "RATE (₹)", "AMOUNT (₹)"),
  ];

  // Material breakdown by type
  const byMat = {};
  for (const eId of Object.keys(st.edges)) {
    const m = getWallMetrics(eId, st);
    if (!m) continue;
    if (!byMat[m.mat.id]) byMat[m.mat.id] = { mat: m.mat, vol: 0, cost: 0, bricks: 0, cement: 0, sand: 0 };
    byMat[m.mat.id].vol    += m.netVol;
    byMat[m.mat.id].cost   += m.matCost;
    byMat[m.mat.id].bricks += m.bricks;
    byMat[m.mat.id].cement += m.cement;
    byMat[m.mat.id].sand   += m.sand;
  }

  let wallTotal = 0, labourTotal = 0, roomTotal = 0;

  lines.push(row("MASONRY WORKS", "", "", "", "", ""));
  for (const { mat, vol, cost } of Object.values(byMat)) {
    lines.push(row("", mat.name, vol.toFixed(3), "m³", Math.round(mat.rateM3 || mat.rateM2 || 0), Math.round(cost)));
    wallTotal += cost;
  }
  labourTotal = wallTotal * 0.25;
  lines.push(row("", "Labour (25%)", "", "", "", Math.round(labourTotal)));
  lines.push(row("", "Masonry Subtotal", "", "", "", Math.round(wallTotal + labourTotal)));
  lines.push(row(""));

  lines.push(row("FINISHES", "", "", "", "", ""));
  for (const faceId of Object.keys(st.faces || {})) {
    const m = getRoomMetrics(faceId, st);
    if (!m) continue;
    const name = m.rd.name || m.rd.type || "Room";
    lines.push(row("", `${name} - Floor`, m.areaM2.toFixed(2), "m²", m.tmpl.floorR, Math.round(m.floorCost)));
    lines.push(row("", `${name} - Wall Finish`, m.wallAreaM2.toFixed(2), "m²", m.tmpl.wallR, Math.round(m.wallCost)));
    roomTotal += m.totalCost;
  }
  lines.push(row("", "Finishes Subtotal", "", "", "", Math.round(roomTotal)));
  lines.push(row(""));

  const grandTotal = wallTotal + labourTotal + roomTotal;
  lines.push(row("GRAND TOTAL", "", "", "", "", Math.round(grandTotal)));
  lines.push(row(""));
  lines.push(row("MATERIAL QUANTITIES"));
  lines.push(row("", "Bricks", Object.values(byMat).reduce((s, m) => s + m.bricks, 0), "nos.", "", ""));
  lines.push(row("", "Cement", Object.values(byMat).reduce((s, m) => s + m.cement, 0).toFixed(1), "bags", "", ""));
  lines.push(row("", "Sand",   Object.values(byMat).reduce((s, m) => s + m.sand,   0).toFixed(1), "cft", "", ""));

  downloadCSV(lines.join("\n"), outFile);
}
